import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InboxIcon from '@mui/icons-material/Inbox';
import { useQuery } from '@tanstack/react-query';
import {
  ApprovalInboxItemSchema,
  MyOnboardingTaskSchema,
  UnreadCountSchema,
  type ApprovalInboxItem,
  type MeResponse,
} from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { errorMessage, getData } from '../../shared/api/client';
import { hasCapability } from '../../shared/session/session';
import { SubjectLink } from '../approvals/ApprovalsPage';
import { INBOX, INBOX_KEY, absoluteTime, relativeTime } from '../notifications/inbox';

/** How many of the oldest waiting approvals the card lists by name. */
const OLDEST = 3;

/**
 * What is waiting for the signed-in user (AI plan TAQON-PLAN-AI-001, Phase 1): the approvals
 * waiting on them (FRD-CORE-018), their open onboarding tasks (FRD-HRP-102) and their unread
 * notifications (§4.4), each linked to the page that holds it.
 *
 * It reads the same endpoints, with the same query keys, as the My approvals page, My onboarding
 * tasks and the notification bell, so it shows exactly what they show and adds no rule of its own.
 * A part appears only when the session holds the permission its endpoint requires, and fails on
 * its own. The card is absent when no part applies.
 */
export function WaitingForYouCard({ me, today }: { me: MeResponse; today: string }) {
  const { approvals, onboarding, notifications } = parts(me);
  if (!approvals && !onboarding && !notifications) return null;
  return (
    <Paper component="section" aria-label="Waiting for you" sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, mb: 2 }}
      >
        <InboxIcon color="primary" aria-hidden /> Waiting for you
      </Typography>
      <Stack spacing={2.5}>
        {approvals && <Approvals me={me} />}
        {onboarding && <OnboardingTasks today={today} />}
        {notifications && <Notifications />}
      </Stack>
    </Paper>
  );
}

function parts(me: MeResponse) {
  return {
    approvals: hasCapability(me, 'platform.approval_step:view'),
    onboarding: hasCapability(me, 'hr.employee:view'),
    notifications: hasCapability(me, 'platform.notification:view'),
  };
}

/** Whether the card has anything to show this session. */
export function waitingForYouShown(me: MeResponse): boolean {
  return Object.values(parts(me)).some(Boolean);
}

function Part({
  title,
  to,
  action,
  children,
}: {
  title: string;
  to: string;
  action: string;
  children: ReactNode;
}) {
  return (
    <Box component="section" aria-label={title}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography variant="subtitle2" component="h3">
          {title}
        </Typography>
        <Button component={RouterLink} to={to} size="small">
          {action}
        </Button>
      </Stack>
      {children}
    </Box>
  );
}

function Approvals({ me }: { me: MeResponse }) {
  const pending = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => getData('/api/v1/approvals/pending', z.array(ApprovalInboxItemSchema)),
  });
  return (
    <Part title="Approvals" to="/approvals" action="My approvals">
      {pending.isPending && <Skeleton width="60%" />}
      {pending.isError && (
        <Alert severity="error" variant="outlined">
          Your approvals could not be loaded: {errorMessage(pending.error)}
        </Alert>
      )}
      {pending.data !== undefined && <ApprovalList items={pending.data} me={me} />}
    </Part>
  );
}

function ApprovalList({ items, me }: { items: readonly ApprovalInboxItem[]; me: MeResponse }) {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing is waiting for your approval.
      </Typography>
    );
  }
  const escalated = items.filter((item) => item.escalated).length;
  const oldest = [...items].sort((a, b) => a.since.localeCompare(b.since)).slice(0, OLDEST);
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        {items.length} waiting for you
        {escalated > 0 && `, ${String(escalated)} escalated`}
        {items.length > OLDEST && ` (the ${String(OLDEST)} oldest below)`}
      </Typography>
      <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {oldest.map((item) => (
          <Box component="li" key={item.stepId}>
            <SubjectLink item={item} />
            {item.escalated && (
              <Chip size="small" label="escalated" color="warning" sx={{ ml: 1 }} />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              sx={{ display: 'block' }}
            >
              {item.subjectRecordTypeName} · {item.stepName}
              {item.delegateOf !== null && ' · as delegate'} · arrived{' '}
              <span title={absoluteTime(item.since, me.businessTimezone)}>
                {relativeTime(item.since)}
              </span>
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

function OnboardingTasks({ today }: { today: string }) {
  const tasks = useQuery({
    queryKey: ['hr', 'onboarding', 'my-tasks'],
    queryFn: () => getData('/api/v1/hr/onboarding/my-tasks', z.array(MyOnboardingTaskSchema)),
  });
  const open = (tasks.data ?? []).filter((task) => !task.done);
  const overdue = open.filter((task) => task.dueDate < today).length;
  return (
    <Part title="Onboarding tasks" to="/hr/my-onboarding-tasks" action="My onboarding tasks">
      {tasks.isPending && <Skeleton width="50%" />}
      {tasks.isError && (
        <Alert severity="error" variant="outlined">
          Your onboarding tasks could not be loaded: {errorMessage(tasks.error)}
        </Alert>
      )}
      {tasks.data !== undefined && (
        <Typography variant="body2" color={open.length === 0 ? 'text.secondary' : 'text.primary'}>
          {open.length === 0
            ? 'No open onboarding task is assigned to you.'
            : `${String(open.length)} open`}
          {overdue > 0 && (
            <>
              ,{' '}
              <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                {overdue} overdue
              </Box>
            </>
          )}
        </Typography>
      )}
    </Part>
  );
}

function Notifications() {
  // The bell's own query: one request serves both, and reading a notification refreshes both.
  const unread = useQuery({
    queryKey: [...INBOX_KEY, 'unread-count'],
    queryFn: () => getData(`${INBOX}/unread-count`, UnreadCountSchema),
  });
  return (
    <Part title="Notifications" to="/notifications" action="All notifications">
      {unread.isPending && <Skeleton width="40%" />}
      {unread.isError && (
        <Alert severity="error" variant="outlined">
          Your notifications could not be loaded: {errorMessage(unread.error)}
        </Alert>
      )}
      {unread.data !== undefined && (
        <Typography
          variant="body2"
          color={unread.data.unread === 0 ? 'text.secondary' : 'text.primary'}
        >
          {unread.data.unread === 0
            ? 'No unread notification.'
            : `${String(unread.data.unread)} unread`}
        </Typography>
      )}
    </Part>
  );
}
